import React, { useEffect, useState } from "react";
import {
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  FormGroup,
  Badge,
  Col,
  Row,
} from "reactstrap";
import { AlternativePagination } from "../../components/Common/Table";
import "../../global.css";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import AWSS3Uploader from "../../components/Common/S3uploader";
import {
  AddStores,
  DeleteStores,
  UpdateStores,
  GetStores,
  GetCities,
  AssignStoreAdmin,
} from "../../requests";
import LoadingSpinner from "../../components/LoadingSpinner";
import { toast } from "react-toastify";

const Stores = () => {
  document.title = "Stores | Susipizza";

  const [modal, setModal] = useState(false);
  const [storeImage, setStoreImage] = useState("");
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [alternatePhone, setAlternatePhone] = useState("");
  const [description, setDescription] = useState("");
  const [openingTime, setOpeningTime] = useState("");
  const [closingTime, setClosingTime] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [locationName, setLocationName] = useState("");
  const [email, setEmail] = useState("");
  const [deliveryRadius, setDeliveryRadius] = useState("");
  const [deliveryEnabled, setDeliveryEnabled] = useState(true);
  const [pickupEnabled, setPickupEnabled] = useState(true);
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("");
  const [status, setStatus] = useState("");
  const [StoresData, setStoresData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ButtonText, setButtonText] = useState("");
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const [targetId, setTargetId] = useState(null);

  // Assign admin modal states
  const [adminModal, setAdminModal] = useState(false);
  const [activeStore, setActiveStore] = useState(null);
  const [managerName, setManagerName] = useState("");
  const [managerEmail, setManagerEmail] = useState("");
  const [managerPhone, setManagerPhone] = useState("");
  const [generatedPassword, setGeneratedPassword] = useState("");

  const toggle = () => setModal(!modal);
  const toggleAdminModal = () => {
    setAdminModal(!adminModal);
    if (adminModal) {
      // reset admin form on close
      setManagerName("");
      setManagerEmail("");
      setManagerPhone("");
      setGeneratedPassword("");
    }
  };

  const columns = [
    {
      name: <span className="font-weight-bold fs-13">SR No.</span>,
      selector: (row, index) => (index + 1).toString(),
      width: "70px",
    },
    {
      name: <span className="font-weight-bold fs-13">Image</span>,
      cell: (row) => (
        <img
          src={row.storeImage}
          alt={row.name}
          style={{ width: 48, height: 48, borderRadius: 8, objectFit: "cover", border: "1px solid #e5e7eb" }}
        />
      ),
      width: "80px",
    },
    {
      name: <span className="font-weight-bold fs-13">Name</span>,
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: <span className="font-weight-bold fs-13">Address</span>,
      cell: (row) => (
        <div className="store-address-cell" title={row.address}>
          {row.address}
        </div>
      ),
      grow: 2,
      maxWidth: "260px",
    },
    {
      name: <span className="font-weight-bold fs-13">Phone</span>,
      selector: (row) => row.phoneNumber,
      minWidth: "120px",
    },
    {
      name: <span className="font-weight-bold fs-13">City</span>,
      selector: (row) => row.city,
    },
    {
      name: <span className="font-weight-bold fs-13">Services</span>,
      cell: (row) => (
        <div className="d-flex flex-column gap-1">
          <Badge color={row.deliveryEnabled !== false ? "success" : "secondary"} pill>
            🚚 {row.deliveryEnabled !== false ? "Delivery ON" : "Delivery OFF"}
          </Badge>
          <Badge color={row.pickupEnabled !== false ? "success" : "secondary"} pill>
            🏪 {row.pickupEnabled !== false ? "Pickup ON" : "Pickup OFF"}
          </Badge>
        </div>
      ),
    },
    {
      name: <span className="font-weight-bold fs-13">Status</span>,
      cell: (row) => (
        <Badge color={row.status === "active" ? "success" : "danger"} pill>
          {row.status}
        </Badge>
      ),
    },
    {
      name: <span className="font-weight-bold fs-13">Action</span>,
      cell: (row) => (
        <div className="d-flex gap-2 align-items-center">
          <button className="btn btn-sm btn-icon btn-outline-info" onClick={() => handleAssignAdminClick(row)} title="Assign Store Admin">
            <span className="mdi mdi-shield-account" />
          </button>
          <button className="btn btn-sm btn-icon btn-outline-primary" onClick={() => handleEdit(row)} title="Edit Store">
            <span className="mdi mdi-pencil" />
          </button>
          <button className="btn btn-sm btn-icon btn-outline-danger" onClick={() => handleDeleteClick(row.id)} title="Delete Store">
            <span className="mdi mdi-trash-can-outline" />
          </button>
        </div>
      ),
      width: "150px"
    },
  ];

  const handleAssignAdminClick = (store) => {
    setActiveStore(store);
    setAdminModal(true);
  };

  const handleSaveAdmin = async () => {
    if (!managerName || !managerEmail) {
      toast.warn("Name and Email are required to assign a store admin.");
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const resp = await AssignStoreAdmin(token, activeStore.storeId, {
        name: managerName,
        email: managerEmail,
        phoneNumber: managerPhone
      });
      const tempPass = resp.data?.data?.tempPassword || resp.data?.tempPassword;
      setGeneratedPassword(tempPass);
      toast.success("Store Admin account created successfully!");
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Failed to create Store Admin account.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (id) => {
    setTargetId(id);
    setConfirmModal(true);
  };

  const confirmDelete = async () => {
    setConfirmModal(false);
    if (!targetId) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      await DeleteStores(token, targetId);
      GetStoresData();
      toast.success("Store deleted successfully");
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
      setTargetId(null);
    }
  };

  const GetStoresData = async () => {
    try {
      setLoading(true);
      const resp = await GetStores();
      setStoresData(resp.data);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const GetCitiesData = async () => {
    try {
      const resp = await GetCities();
      const data = resp.data && Array.isArray(resp.data) ? resp.data : (resp.data?.data || resp || []);
      setCities(data);
    } catch (err) {
      console.error("Failed to load cities", err);
    }
  };

  useEffect(() => {
    GetStoresData();
    GetCitiesData();
  }, []);

  const resetForm = () => {
    setName(""); setAddress(""); setPhone(""); setAlternatePhone("");
    setStoreImage(""); setDescription(""); setOpeningTime(""); setClosingTime("");
    setLatitude(""); setLongitude(""); setLocationName(""); setEmail("");
    setDeliveryRadius(""); setDeliveryEnabled(true); setPickupEnabled(true);
    setCity(""); setStatus(""); setSelectedStoreId(null);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const payload = {
        name, address,
        phoneNumber: phone,
        alternatePhoneNumber: alternatePhone,
        storeImage, description, openingTime, closingTime,
        location: { lat: parseFloat(latitude) || 0, lng: parseFloat(longitude) || 0 },
        locationName, email,
        deliveryRadius: parseFloat(deliveryRadius) || 0,
        deliveryEnabled: !!deliveryEnabled,
        pickupEnabled: !!pickupEnabled,
        city, status,
      };

      if (ButtonText === "Update") {
        await UpdateStores(token, selectedStoreId, payload);
        toast.success("Store updated successfully");
      } else {
        await AddStores(token, payload);
        toast.success("Store added successfully");
      }

      toggle();
      resetForm();
      GetStoresData();
    } catch (err) {
      console.error("Save store error:", err.response?.data || err);
      const validationErrs = err.response?.data?.validationErrors;
      if (validationErrs && Array.isArray(validationErrs)) {
        validationErrs.forEach(e => toast.error(`${e.field}: ${e.message}`));
      } else {
        toast.error(err.response?.data?.error || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rowData) => {
    setButtonText("Update");
    setModal(true);
    setName(rowData.name);
    setAddress(rowData.address);
    setPhone(rowData.phoneNumber);
    setAlternatePhone(rowData.alternatePhoneNumber);
    setStoreImage(rowData.storeImage);
    setDescription(rowData.description);
    setOpeningTime(rowData.openingTime);
    setClosingTime(rowData.closingTime);
    const lat = rowData.location?.lat !== undefined ? rowData.location.lat : (rowData.latitude || 0);
    const lng = rowData.location?.lng !== undefined ? rowData.location.lng : (rowData.longitude || 0);
    setLatitude(lat.toString());
    setLongitude(lng.toString());
    setLocationName(rowData.locationName || "");
    setEmail(rowData.email || "");
    setDeliveryRadius(rowData.deliveryRadius !== undefined && rowData.deliveryRadius !== null ? rowData.deliveryRadius.toString() : "");
    setDeliveryEnabled(rowData.deliveryEnabled !== false);
    setPickupEnabled(rowData.pickupEnabled !== false);
    setCity(rowData.city || "");
    setStatus(rowData.status);
    setSelectedStoreId(rowData.id);
  };

  const SectionHeader = ({ icon, title }) => (
    <div style={{
      display: "flex", alignItems: "center", gap: 8,
      fontSize: 12, fontWeight: 700, textTransform: "uppercase",
      letterSpacing: "0.08em", color: "#f4632a",
      borderBottom: "1px solid #f3f4f6", paddingBottom: 8, marginBottom: 14
    }}>
      <span className={`mdi ${icon}`} style={{ fontSize: 15 }} />
      {title}
    </div>
  );

  return (
    <React.Fragment>
      <LoadingSpinner loading={loading} />
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs title="susipizza" breadcrumbItem="Stores" />
          <button
            type="button"
            className="btn btn-primary mb-3"
            onClick={() => { setModal(true); setButtonText("Register"); resetForm(); }}
          >
            <span className="mdi mdi-plus me-1" />
            Add Store
          </button>
          <AlternativePagination columns={columns} data={StoresData} />
        </Container>
      </div>

      {/* Add / Edit Store Modal */}
      <Modal isOpen={modal} toggle={toggle} centered size="xl">
        <ModalHeader
          toggle={toggle}
          style={{
            background: "linear-gradient(135deg, #f4632a 0%, #e85820 100%)",
            color: "#fff",
            borderRadius: "8px 8px 0 0",
            padding: "16px 24px",
          }}
        >
          <span className="mdi mdi-store me-2" />
          {ButtonText === "Update" ? `Edit Store — ${name}` : "Add New Store"}
        </ModalHeader>

        <ModalBody style={{ background: "#fafafa", padding: "0" }}>
          <div style={{ display: "flex", gap: 0, minHeight: 520 }}>

            {/* LEFT PANEL: Image */}
            <div style={{
              width: 240, flexShrink: 0,
              background: "#fff",
              borderRight: "1px solid #f0f0f0",
              padding: "24px 20px",
              display: "flex", flexDirection: "column", alignItems: "center", gap: 16
            }}>
              <div style={{
                width: 160, height: 160, borderRadius: 12,
                overflow: "hidden", border: "2px dashed #e5e7eb",
                background: "#f9fafb",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {storeImage ? (
                  <img
                    src={storeImage}
                    alt="Store"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <div style={{ textAlign: "center", color: "#9ca3af" }}>
                    <span className="mdi mdi-image-outline" style={{ fontSize: 40 }} />
                    <div style={{ fontSize: 11, marginTop: 4 }}>No image yet</div>
                  </div>
                )}
              </div>

              {name && (
                <div style={{
                  fontSize: 13, fontWeight: 600, textAlign: "center",
                  color: "#374151", padding: "6px 12px",
                  background: "#f3f4f6", borderRadius: 8, width: "100%",
                }}>
                  {name}
                </div>
              )}

              {status && (
                <Badge
                  color={status === "active" ? "success" : "danger"}
                  pill
                  style={{ fontSize: 12, padding: "5px 14px" }}
                >
                  {status === "active" ? "🟢 Active" : "🔴 Inactive"}
                </Badge>
              )}

              <div style={{ width: "100%" }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Store Image
                </div>
                <AWSS3Uploader onUploadComplete={(url) => setStoreImage(url)} folder="stores" />
              </div>
            </div>

            {/* RIGHT PANEL: Form */}
            <div style={{ flex: 1, padding: "24px 28px", overflowY: "auto" }}>
              <SectionHeader icon="mdi-information-outline" title="Basic Information" />
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Store Name *</Label>
                    <Input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Susi Pizza Harmu" />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Email Address</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="store@susipizza.com" />
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Address</Label>
                <Input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Full street address" />
              </FormGroup>
              <FormGroup>
                <Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Description</Label>
                <Input type="textarea" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description of this store..." />
              </FormGroup>

              <SectionHeader icon="mdi-phone-outline" title="Contact" />
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Primary Phone *</Label>
                    <Input type="text" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. 9876543210" />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Alternate Phone</Label>
                    <Input type="text" value={alternatePhone} onChange={(e) => setAlternatePhone(e.target.value)} placeholder="e.g. 9876543211" />
                  </FormGroup>
                </Col>
              </Row>

              <SectionHeader icon="mdi-map-marker-outline" title="Coordinates" />
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Latitude</Label>
                    <Input type="text" value={latitude} onChange={(e) => setLatitude(e.target.value)} placeholder="23.3456" />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Longitude</Label>
                    <Input type="text" value={longitude} onChange={(e) => setLongitude(e.target.value)} placeholder="85.3456" />
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Location Name</Label>
                    <Input type="text" value={locationName} onChange={(e) => setLocationName(e.target.value)} placeholder="e.g. Harmu, Ranchi" />
                  </FormGroup>
                </Col>
              </Row>

              <SectionHeader icon="mdi-cog-outline" title="Operations" />
              <Row>
                <Col md={3}>
                  <FormGroup>
                    <Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Opening Time</Label>
                    <Input type="time" value={openingTime} onChange={(e) => setOpeningTime(e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Closing Time</Label>
                    <Input type="time" value={closingTime} onChange={(e) => setClosingTime(e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Delivery Radius (KM)</Label>
                    <Input type="number" value={deliveryRadius} onChange={(e) => setDeliveryRadius(e.target.value)} placeholder="5" />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>City</Label>
                    <Input type="select" value={city} onChange={(e) => setCity(e.target.value)}>
                      <option value="">Select City</option>
                      {cities.map((c) => (
                        <option key={c.id || c._id} value={c.name}>{c.name}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <FormGroup>
                    <Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Delivery</Label>
                    <Input type="select" value={deliveryEnabled ? "true" : "false"} onChange={(e) => setDeliveryEnabled(e.target.value === "true")}>
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Pickup</Label>
                    <Input type="select" value={pickupEnabled ? "true" : "false"} onChange={(e) => setPickupEnabled(e.target.value === "true")}>
                      <option value="true">Enabled</option>
                      <option value="false">Disabled</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={4}>
                  <FormGroup>
                    <Label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Status</Label>
                    <Input type="select" value={status} onChange={(e) => setStatus(e.target.value)}>
                      <option value="">Select Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>
            </div>
          </div>
        </ModalBody>

        <ModalFooter style={{ background: "#fff", borderTop: "1px solid #f0f0f0", padding: "12px 24px" }}>
          <Button
            onClick={handleSave}
            style={{
              background: "linear-gradient(135deg, #f4632a, #e85820)",
              border: "none", borderRadius: 8,
              padding: "9px 28px", fontWeight: 600,
            }}
          >
            <span className="mdi mdi-content-save me-1" />
            {ButtonText === "Update" ? "Save Changes" : "Register Store"}
          </Button>
          <Button color="light" onClick={() => { toggle(); resetForm(); }} style={{ borderRadius: 8, padding: "9px 20px" }}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Assign Store Admin Modal */}
      <Modal isOpen={adminModal} toggle={toggleAdminModal} centered>
        <ModalHeader toggle={toggleAdminModal}>
          Assign Manager Admin — {activeStore?.name}
        </ModalHeader>
        <ModalBody>
          {generatedPassword ? (
            <div className="alert alert-success text-center py-3">
              <h5>Store Admin Assigned Successfully!</h5>
              <p className="mb-2">Please share these temporary login credentials with the store manager:</p>
              <div className="bg-white border rounded p-2 text-start font-mono font-size-14">
                <strong>Login Username / Email:</strong> {managerEmail}<br/>
                <strong>Temporary Password:</strong> <span className="text-danger font-weight-bold">{generatedPassword}</span>
              </div>
              <small className="text-muted d-block mt-2">The manager will be prompted to change their password on first login.</small>
            </div>
          ) : (
            <>
              <FormGroup>
                <Label for="managerName">Manager Name *</Label>
                <Input
                  type="text"
                  id="managerName"
                  value={managerName}
                  onChange={(e) => setManagerName(e.target.value)}
                  placeholder="e.g. John Manager"
                />
              </FormGroup>
              <FormGroup>
                <Label for="managerEmail">Manager Email / Username *</Label>
                <Input
                  type="email"
                  id="managerEmail"
                  value={managerEmail}
                  onChange={(e) => setManagerEmail(e.target.value)}
                  placeholder="e.g. manager@susipizza.com"
                />
              </FormGroup>
              <FormGroup>
                <Label for="managerPhone">Manager Phone</Label>
                <Input
                  type="text"
                  id="managerPhone"
                  value={managerPhone}
                  onChange={(e) => setManagerPhone(e.target.value)}
                  placeholder="e.g. 9876543210"
                />
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {generatedPassword ? (
            <Button color="primary" onClick={toggleAdminModal}>Close</Button>
          ) : (
            <>
              <Button color="light" onClick={toggleAdminModal}>Cancel</Button>
              <Button color="primary" onClick={handleSaveAdmin}>Assign Manager</Button>
            </>
          )}
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={confirmModal} toggle={() => setConfirmModal(!confirmModal)} centered size="sm">
        <ModalHeader toggle={() => setConfirmModal(!confirmModal)}>
          <span className="mdi mdi-alert-outline me-2" style={{ color: "#ef4444" }} />
          Confirm Deletion
        </ModalHeader>
        <ModalBody style={{ fontSize: 14, color: "#374151" }}>
          Are you sure you want to delete this store? This action <strong>cannot be undone</strong>.
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={confirmDelete}>
            <span className="mdi mdi-delete me-1" />
            Delete
          </Button>
          <Button color="light" onClick={() => setConfirmModal(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Stores;
